/**
 * streetgrow.ts — era'd TENSOR-FIELD secondary street growth under the GLOBAL
 * PLANARITY INVARIANT (W2d-2 Task 3). REPLACES the space-colonization skeleton +
 * densify (both retired) for every VILLAGE+ settlement.
 *
 * THE INVARIANT (spec §AMENDMENT 2026-07-09, rule #0, BINDING): no two edges may
 * cross without a shared node. Every accepted segment insertion that would cross
 * ANY existing edge (primary, secondary, or hamlet lane) applies EXTEND-TO-MEET:
 * it truncates at the intersection, SPLITS the crossed edge into two, and inserts a
 * junction node there. The segment-vs-edge test runs over a uniform-grid spatial
 * hash (edge AABB buckets) so the metro budget stays tractable — the naive N²
 * sweep would blow it. `crossings == 0` and `ladder == 0` are the standing oracles
 * (worldgen-growth-era.test.mts); the hop-count densify gate that self-propagated
 * parallel-chain ladders is GONE — loop closure here is implicit (a segment that
 * meets an edge junctions there) and gated by the min-junction-angle test, never a
 * raw hop count.
 *
 * THE GROWER (spec §"Core growth parameters", per settlement tier ≥ VILLAGE): a
 * priority queue of growth FRONTS ordered by distance-from-seed ascending (index
 * tie-break) with a HARD per-tier iteration budget (hash-rolled in the spec range)
 * so truncation degrades the EDGE, not the core (spec §"Implementation warning").
 * Fronts seed from the settlement's PRIMARY-road nodes (re-derived via the anchor
 * set — carry #2; primaries are era-0/epoch-0 and stay UNLOGGED — carry #3). Era
 * bands partition the settlement radius (old core innermost); a front's era is a
 * pure function of its radial distance, and because inner fronts pop first the
 * old-core edges naturally carry the LOWER epochs (banded, spec §"Eras = epochs").
 * Each popped front spawns a segment of spec length ± jitter along
 * {@link tensorDirAt}(…, eraBand) + heading noise, then runs the RULE PIPELINE in
 * order:
 *   (1) snap-to-node within the tier snap radius;
 *   (2) planar check + extend-to-meet split (rule #0, above);
 *   (3) min-junction-angle reject — the tier table value lerped 50% toward the
 *       settlement culture's `minAngleDeg` (the same 50%-culture modulation the
 *       tensor module applies to gridW/noise; orchestrator resolution of the brief
 *       ambiguity, documented at {@link minAngleRad});
 *   (4) max-degree reject (either endpoint).
 * Accepted segments are EDGE_STREET (width classes are W2d-3) and log LOG_EXTEND;
 * a new front continues the heading, and with the spec branch probability a second
 * front spawns rotated ±90° (registered salt).
 *
 * HAMLET LANES (carry #6/#8): a HAMLET seed co-located inside a VILLAGE+ cell grows
 * as a short one-lane hamlet ATTACHED to the SAME planar net — its lane segments
 * snap/split against everything, primaries included. There is ONE {@link PlanarNet}
 * / one edge set per region.
 *
 * OUTPUT. The grower snapshots the primary network out of the incoming builder,
 * grows into a PlanarNet (where an edge CAN be split — the builder is append-only),
 * and flushes a FRESH {@link CityGraphBuilder}: primaries re-emitted UNLOGGED at
 * epoch 0 (halves of a split primary keep both), secondaries EDGE_STREET logged
 * LOG_EXTEND at their era-banded epoch. Downstream (pruneDeadEnds → emitParcels →
 * pruneUtility) is unchanged.
 *
 * DETERMINISM. Every roll is a registered hashInts stream; the heap breaks ties by
 * insertion sequence; every spatial-hash query gathers candidates then tests in a
 * fixed order (membership/any-hit only — bucket order cannot leak); pmath only, no
 * `**`, no Date/Math.random (the pmath-ban + salt-registry gates cover this file).
 */
import * as pmath from "../pmath.js";
import { PlanetSampler, type TerrainSample } from "../universe.js";
import { hashSeed } from "../rng.js";
import { type CellId } from "./cells.js";
import { packCell } from "./cells.js";
import { hashInts, h01 } from "./hash.js";
import {
  type RegionFrame, CityGraphBuilder, localToDir,
  NODE_SEED, NODE_PORT, NODE_JUNCTION,
  EDGE_ARTERIAL, EDGE_STREET, EDGE_BRIDGE, LOG_EXTEND,
} from "./citygraph.js";
import { TIER_VILLAGE, TIER_HAMLET } from "./tierRegistry.js";
import { type RouteTerrain, LATTICE_M, makeSamplerTerrain } from "./router.js";
import {
  type TensorCtx, cultureFor, gridOrientationFor, tensorDirAt, headingNoiseDeg,
} from "./tensorfield.js";
import { TIER_PARAMS, dequantizePrimary, CHAIKIN_ROUNDS, makeInCell } from "./growth.js";

const DEG2RAD = Math.PI / 180;

// Registered salts (see hash.ts): the era grower's per-segment length jitter and
// its per-front branch roll. Heading noise reuses the tensor module's 0x4e15
// (headingNoiseDeg), and the per-tier iteration BUDGET rolls off SALT_BUDGET.
const SALT_SEGLEN = 0x5ec0; //  segment length jitter (per accepted segment)
const SALT_BRANCH = 0xb42c; //  branch spawn roll + branch side
const SALT_BUDGET = 0x6d17; //  per-settlement iteration-budget roll
const SALT_ERAS = 0xe1a5;   //  per-settlement era-COUNT roll (ranged tiers, e.g. metro 4–5)

// ── per-tier secondary-growth parameters (spec §"Core growth parameters") ──────

/** One tier's secondary-growth knobs (spec table, BINDING verbatim). */
export interface SecondaryParams {
  /** Segment length band [min,max] (m); the nominal is the midpoint. */
  segMinM: number; segMaxM: number;
  /** Segment length jitter fraction (± of nominal). */
  jitterFrac: number;
  /** Snap-to-node radius (m). */
  snapRadiusM: number;
  /** Extend-to-meet reach as a multiple of the segment length. */
  extendMult: number;
  /** Min junction angle (deg) — culture-blended at {@link minAngleRad}. */
  minAngleDeg: number;
  /** Max node degree. */
  maxDegree: number;
  /** Branch probability per accepted segment. */
  branchProb: number;
  /** Iteration budget band [min,max] (hash-rolled, hard cap). */
  budgetMin: number; budgetMax: number;
  /**
   * Era count (radius bands, old core innermost). This is the LOW end of the
   * spec range; a tier whose spec table gives a RANGE (e.g. metropolis "4–5")
   * also sets {@link erasMax} and the effective count is hash-rolled per
   * settlement in [eras, erasMax] (SALT_ERAS), exactly as budgets/anchor counts
   * are rolled within their ranges. `tensorDirAt`/`eraOf` clamp the outer band
   * to 3 (index ≤ 2), so a 5th era collapses onto the outermost tensor band.
   */
  eras: number;
  /** Optional HIGH end of the era-count range (ranged tiers only; else = eras). */
  erasMax?: number;
}

/**
 * Secondary params indexed by tier 0..5 (spec §"Core growth parameters", verbatim).
 * Only village(2)/town(3)/city(4)/metropolis(5) grow secondaries; waystation(0)/
 * hamlet(1) reuse the village row defensively (the grower never calls them below
 * village except the hamlet-lane path, which uses {@link HAMLET_LANE}).
 */
export const SECONDARY_PARAMS: readonly SecondaryParams[] = [
  // 0 waystation → village row (defensive; never grown)
  { segMinM: 30, segMaxM: 45, jitterFrac: 0.40, snapRadiusM: 12, extendMult: 1.1, minAngleDeg: 25, maxDegree: 3, branchProb: 0.25, budgetMin: 40, budgetMax: 80, eras: 1 },
  // 1 hamlet → village row (defensive; the lane path uses HAMLET_LANE)
  { segMinM: 30, segMaxM: 45, jitterFrac: 0.40, snapRadiusM: 12, extendMult: 1.1, minAngleDeg: 25, maxDegree: 3, branchProb: 0.25, budgetMin: 40, budgetMax: 80, eras: 1 },
  // 2 village
  { segMinM: 30, segMaxM: 45, jitterFrac: 0.40, snapRadiusM: 12, extendMult: 1.1, minAngleDeg: 25, maxDegree: 3, branchProb: 0.25, budgetMin: 40, budgetMax: 80, eras: 1 },
  // 3 town
  { segMinM: 25, segMaxM: 40, jitterFrac: 0.30, snapRadiusM: 10, extendMult: 1.2, minAngleDeg: 30, maxDegree: 4, branchProb: 0.35, budgetMin: 150, budgetMax: 300, eras: 2 },
  // 4 city
  { segMinM: 20, segMaxM: 35, jitterFrac: 0.25, snapRadiusM: 8, extendMult: 1.3, minAngleDeg: 35, maxDegree: 4, branchProb: 0.45, budgetMin: 500, budgetMax: 1000, eras: 3 },
  // 5 metropolis (spec eras "4–5" → hash-rolled 4|5 per settlement; tensorDirAt/
  // eraOf clamp the outermost band to 3, so the 5th era collapses onto it)
  { segMinM: 18, segMaxM: 30, jitterFrac: 0.20, snapRadiusM: 8, extendMult: 1.3, minAngleDeg: 40, maxDegree: 5, branchProb: 0.55, budgetMin: 1500, budgetMax: 3000, eras: 4, erasMax: 5 },
];

/**
 * A hamlet lane's parameters when a HAMLET seed grows inside a VILLAGE+ cell
 * (carry #6/#8). One organic lane — village geometry, tiny budget, no branching,
 * one era — attached to the shared net so it snaps/splits like any secondary.
 */
const HAMLET_LANE: SecondaryParams = {
  segMinM: 26, segMaxM: 40, jitterFrac: 0.35, snapRadiusM: 12, extendMult: 1.1,
  minAngleDeg: 25, maxDegree: 3, branchProb: 0, budgetMin: 12, budgetMax: 24, eras: 1,
};

const EPS = 1e-9;

// d_sep (braid fix): minimum LATERAL spacing between roughly-parallel streets
// that don't share a junction, as a fraction of the tier's segMinM — city/metro
// era-INFILL legitimately lays parallels at ~half a lattice step (~10 m on
// segMin 20), so the bound must ride the tier's own scale (a flat 12 m gutted
// the calibration city 172→33 parcels). 0.42 × segMin: city 8.4 m (infill
// safe), village/hamlet 12.6 m (kills the 8–12 m contour-strand braid core).
const D_SEP_FRAC = 0.42;
const D_SEP_COS = 0.9;

/** Distance from point (px,pv) to segment (au,av)→(bu,bv). */
function segPointDist(au: number, av: number, bu: number, bv: number, px: number, pv: number): number {
  const ux = bu - au, uy = bv - av;
  const l2 = ux * ux + uy * uy;
  if (l2 < EPS) return pmath.hypot(px - au, pv - av);
  let t = ((px - au) * ux + (pv - av) * uy) / l2;
  if (t < 0) t = 0; else if (t > 1) t = 1;
  return pmath.hypot(px - (au + t * ux), pv - (av + t * uy));
}
const EDGE_GRID_M = 40; // edge-AABB spatial-hash cell (≈ segMax) — one local scan
const NODE_GRID_M = 20; // node spatial-hash cell (snap queries)
const NODE_ON_EDGE_M = 1.5; // perpendicular band: a node this close to a segment's interior is "on" it
// R1 D2: far-overgrow primary nodes are clipped to border + this collar (a primary
// may still touch the immediate border zone — that stays within the chart-overlap
// seam band the p2 oracle excludes — but never reaches DEEP into a neighbour cell).
const REPAIR_MIN_ANGLE = 15 * (Math.PI / 180); // below this a repaired crossing drops a street, not junctions
/** Streets never climb cliffs (post-W2d-4 fix): a candidate segment steeper than
 *  this rise/run (tan 25°) is rejected — the front tries other headings. Local
 *  streets switchback; the tensor terrain weight steers, this is the hard floor. */
const SECONDARY_MAX_GRADE = 0.47;

// ── planar net ─────────────────────────────────────────────────────────────────

/**
 * A mutable planar street graph that can SPLIT an edge (the append-only
 * {@link CityGraphBuilder} cannot). Nodes carry (u,v,kind); edges carry
 * (a,b,kind,epoch,logged); a uniform edge-AABB grid answers "does N→P cross any
 * edge?" and a node grid answers "nearest node to P within r?". Dead (split)
 * edges are tombstoned in the grid and skipped. {@link flush} freezes the survivors
 * into a fresh builder.
 */
export class PlanarNet {
  nu: number[] = [];
  nv: number[] = [];
  nk: number[] = [];
  /** node → incident ALIVE edge indices. */
  inc: number[][] = [];
  ea: number[] = [];
  eb: number[] = [];
  ek: number[] = [];
  eEpoch: number[] = [];
  eLogged: boolean[] = [];
  eAlive: boolean[] = [];
  private egrid = new Map<string, number[]>();
  private ngrid = new Map<string, number[]>();
  /** Unordered "a:b" → ALIVE edge index — the duplicate-edge guard (W2d-3). */
  private ekeys = new Map<string, number>();

  addNode(u: number, v: number, kind: number): number {
    const i = this.nu.length;
    this.nu.push(u); this.nv.push(v); this.nk.push(kind); this.inc.push([]);
    const k = Math.floor(u / NODE_GRID_M) + "," + Math.floor(v / NODE_GRID_M);
    let b = this.ngrid.get(k);
    if (b === undefined) { b = []; this.ngrid.set(k, b); }
    b.push(i);
    return i;
  }

  private smear(ei: number, au: number, av: number, bu: number, bv: number): void {
    const gx0 = Math.floor(Math.min(au, bu) / EDGE_GRID_M), gx1 = Math.floor(Math.max(au, bu) / EDGE_GRID_M);
    const gy0 = Math.floor(Math.min(av, bv) / EDGE_GRID_M), gy1 = Math.floor(Math.max(av, bv) / EDGE_GRID_M);
    for (let gx = gx0; gx <= gx1; gx++) {
      for (let gy = gy0; gy <= gy1; gy++) {
        const k = gx + "," + gy;
        let b = this.egrid.get(k);
        if (b === undefined) { b = []; this.egrid.set(k, b); }
        b.push(ei);
      }
    }
  }

  addEdge(a: number, b: number, kind: number, epoch: number, logged: boolean): number {
    // DUPLICATE GUARD (W2d-3, block-detection finding): the A* reuse discount
    // converges primaries onto shared trunk segments and every route RE-EMITS the
    // shared stretch, so pre-W2d-3 graphs carried up to ~100 exact duplicate
    // (same-node-pair) edges per city cell — invisible to the crossings oracle
    // (identical segments don't cross) but fatal to planar FACE tracing (zero-area
    // 2-cycles) and wasteful downstream (double carve features / ribbons). Edges
    // are straight segments, so same endpoints ⇒ same geometry: adding an edge
    // whose unordered node pair is already ALIVE returns the existing index
    // (first-inserted wins — deterministic; observed dup groups are same-kind).
    const key = a < b ? a + ":" + b : b + ":" + a;
    const dup = this.ekeys.get(key);
    if (dup !== undefined && this.eAlive[dup]) return dup;
    const ei = this.ea.length;
    this.ea.push(a); this.eb.push(b); this.ek.push(kind);
    this.eEpoch.push(epoch); this.eLogged.push(logged); this.eAlive.push(true);
    this.inc[a].push(ei); this.inc[b].push(ei);
    this.ekeys.set(key, ei);
    this.smear(ei, this.nu[a], this.nv[a], this.nu[b], this.nv[b]);
    return ei;
  }

  /** Retire edge `ei`, splitting it at (cx,cy) into (a,J) and (J,b); returns J. */
  splitEdge(ei: number, cx: number, cy: number): number {
    const a = this.ea[ei], b = this.eb[ei];
    const kind = this.ek[ei], epoch = this.eEpoch[ei], logged = this.eLogged[ei];
    this.retire(ei);
    const j = this.addNode(cx, cy, NODE_JUNCTION);
    this.addEdge(a, j, kind, epoch, logged);
    this.addEdge(j, b, kind, epoch, logged);
    return j;
  }

  /** Retire edge `ei`, splitting it into (a,J) and (J,b) at an EXISTING node J on it. */
  splitEdgeUsing(ei: number, j: number): void {
    const a = this.ea[ei], b = this.eb[ei];
    if (j === a || j === b) return; // degenerate
    const kind = this.ek[ei], epoch = this.eEpoch[ei], logged = this.eLogged[ei];
    this.retire(ei);
    this.addEdge(a, j, kind, epoch, logged);
    this.addEdge(j, b, kind, epoch, logged);
  }

  private retire(ei: number): void {
    this.eAlive[ei] = false;
    const rem = (arr: number[]): void => { const k = arr.indexOf(ei); if (k >= 0) arr.splice(k, 1); };
    rem(this.inc[this.ea[ei]]);
    rem(this.inc[this.eb[ei]]);
    const a = this.ea[ei], b = this.eb[ei];
    const key = a < b ? a + ":" + b : b + ":" + a;
    if (this.ekeys.get(key) === ei) this.ekeys.delete(key); // free the pair for re-add
    // Left tombstoned in egrid; queries skip !eAlive.
  }

  /** Retire edge `ei` outright (planarity repair drops a shallow-crossing street). */
  retireEdge(ei: number): void { if (this.eAlive[ei]) this.retire(ei); }

  /**
   * True iff an ALIVE edge NOT incident to `na`/`nb` runs roughly PARALLEL to
   * segment (au,av)→(bu,bv) within `dSep` laterally — the tensor grower's
   * missing d_sep rule (braid fix, 2026-07-11): nothing else bounds LATERAL
   * street spacing (snap acts at endpoints, crossing scans at intersections),
   * so on contour-forced ground fronts trace side-by-side strands 8–16 m apart
   * that read as braided streams. Two tests: direction |cos| ≥ cosMin (crossing
   * streets exit here), and a WHOLE-RUN side-by-side test — BOTH endpoints of
   * one segment within dSep of the other (either orientation, so a long run
   * beside a chain of short edges still matches). Converging WEDGES (one end
   * 3 m out, the other 18 m — ring/spoke transitions approaching a shared
   * junction, all over the designed radial city plans) have unequal end
   * distances and escape; instrumented on the calibration city, 20 of 25
   * matches were such wedges and rejecting them cascaded into a 58% fabric
   * loss. A collinear chain grandparent sits ~1.5 segments BEHIND the front
   * (> dSep along), so straight extension never self-matches; a tight hairpin
   * return DOES match, which is intended — pointy loop-backs are braid look.
   * Merges stay legal: a joining segment survives the junction angle gate only
   * at ≥ minAngle to the target street, which the cos filter ignores.
   * Returns the offending edge's MIDPOINT (the caller aims a perpendicular
   * connector front at it — reject-and-merge, not reject-and-die: flat
   * rejection amputates whole downstream fronts and gutted the calibration
   * city 172→33 parcels), or null when no parallel edge is in range.
   */
  parallelNearby(au: number, av: number, bu: number, bv: number, dSep: number, cosMin: number, na: number, nb: number, exclEdge: number): { mu: number; mv: number } | null {
    let su = bu - au, sv = bv - av;
    const sl = pmath.hypot(su, sv);
    if (sl < EPS) return null;
    su /= sl; sv /= sl;
    const gx0 = Math.floor((Math.min(au, bu) - dSep) / EDGE_GRID_M), gx1 = Math.floor((Math.max(au, bu) + dSep) / EDGE_GRID_M);
    const gy0 = Math.floor((Math.min(av, bv) - dSep) / EDGE_GRID_M), gy1 = Math.floor((Math.max(av, bv) + dSep) / EDGE_GRID_M);
    const seen = new Set<number>();
    for (let gx = gx0; gx <= gx1; gx++) {
      for (let gy = gy0; gy <= gy1; gy++) {
        const bkt = this.egrid.get(gx + "," + gy);
        if (bkt === undefined) continue;
        for (let t = 0; t < bkt.length; t++) {
          const e = bkt[t];
          if (seen.has(e)) continue;
          seen.add(e);
          if (!this.eAlive[e] || e === exclEdge) continue;
          const a2 = this.ea[e], b2 = this.eb[e];
          if (a2 === na || a2 === nb || b2 === na || b2 === nb) continue;
          let eu = this.nu[b2] - this.nu[a2], ev = this.nv[b2] - this.nv[a2];
          const el = pmath.hypot(eu, ev);
          if (el < EPS) continue;
          eu /= el; ev /= el;
          let cos = su * eu + sv * ev;
          if (cos < 0) cos = -cos;
          if (cos < cosMin) continue;
          const oau = this.nu[a2], oav = this.nv[a2], obu = this.nu[b2], obv = this.nv[b2];
          const sideBySide =
            (segPointDist(oau, oav, obu, obv, au, av) <= dSep &&
             segPointDist(oau, oav, obu, obv, bu, bv) <= dSep) ||
            (segPointDist(au, av, bu, bv, oau, oav) <= dSep &&
             segPointDist(au, av, bu, bv, obu, obv) <= dSep);
          if (!sideBySide) continue;
          return { mu: (oau + obu) / 2, mv: (oav + obv) / 2 };
        }
      }
    }
    return null;
  }

  /** Acute angle (rad) between edges e1 and e2's directions (0..π/2). */
  crossAngle(e1: number, e2: number): number {
    let ux = this.nu[this.eb[e1]] - this.nu[this.ea[e1]], uy = this.nv[this.eb[e1]] - this.nv[this.ea[e1]];
    let vx = this.nu[this.eb[e2]] - this.nu[this.ea[e2]], vy = this.nv[this.eb[e2]] - this.nv[this.ea[e2]];
    const ul = pmath.hypot(ux, uy), vl = pmath.hypot(vx, vy);
    if (ul < EPS || vl < EPS) return 0;
    ux /= ul; uy /= ul; vx /= vl; vy /= vl;
    let dot = ux * vx + uy * vy; if (dot < 0) dot = -dot; if (dot > 1) dot = 1;
    return pmath.acos(dot);
  }

  /** Every alive edge index (fixed ascending order). */
  aliveEdges(): number[] {
    const out: number[] = [];
    for (let e = 0; e < this.ea.length; e++) if (this.eAlive[e]) out.push(e);
    return out;
  }

  degree(i: number): number { return this.inc[i].length; }

  /** Nearest node to (u,v) within r (excluding `not`); ties → lower index, -1 none. */
  nearestNode(u: number, v: number, r: number, not: number): number {
    const gx0 = Math.floor((u - r) / NODE_GRID_M), gx1 = Math.floor((u + r) / NODE_GRID_M);
    const gy0 = Math.floor((v - r) / NODE_GRID_M), gy1 = Math.floor((v + r) / NODE_GRID_M);
    let best = -1, bestD = r;
    for (let gx = gx0; gx <= gx1; gx++) {
      for (let gy = gy0; gy <= gy1; gy++) {
        const bkt = this.ngrid.get(gx + "," + gy);
        if (bkt === undefined) continue;
        for (let t = 0; t < bkt.length; t++) {
          const idx = bkt[t];
          if (idx === not) continue;
          const d = pmath.hypot(this.nu[idx] - u, this.nv[idx] - v);
          if (d <= r && (d < bestD || (d === bestD && (best < 0 || idx < best)))) { bestD = d; best = idx; }
        }
      }
    }
    return best;
  }

  /**
   * Nearest crossing of segment (au,av)→(pu,pv) with any ALIVE edge NOT incident
   * to `fromNode` or `exclNode`, gathered over the segment's AABB buckets. Returns
   * the crossing closest to (au,av) as {t (0..1 along the segment), edge, x, y} or
   * null. Any-hit over a fixed candidate order → deterministic.
   */
  nearestCrossing(
    au: number, av: number, pu: number, pv: number, fromNode: number, exclNode: number,
  ): { t: number; edge: number; x: number; y: number } | null {
    const gx0 = Math.floor(Math.min(au, pu) / EDGE_GRID_M), gx1 = Math.floor(Math.max(au, pu) / EDGE_GRID_M);
    const gy0 = Math.floor(Math.min(av, pv) / EDGE_GRID_M), gy1 = Math.floor(Math.max(av, pv) / EDGE_GRID_M);
    let bestT = Infinity, bestE = -1, bx = 0, by = 0;
    const seen = new Set<number>();
    for (let gx = gx0; gx <= gx1; gx++) {
      for (let gy = gy0; gy <= gy1; gy++) {
        const bkt = this.egrid.get(gx + "," + gy);
        if (bkt === undefined) continue;
        for (let bi = 0; bi < bkt.length; bi++) {
          const e = bkt[bi];
          if (seen.has(e)) continue;
          seen.add(e);
          if (!this.eAlive[e]) continue;
          const a = this.ea[e], b = this.eb[e];
          if (a === fromNode || b === fromNode || a === exclNode || b === exclNode) continue;
          const hit = segInt(au, av, pu, pv, this.nu[a], this.nv[a], this.nu[b], this.nv[b]);
          if (hit === null) continue;
          if (hit.t < bestT) { bestT = hit.t; bestE = e; bx = hit.x; by = hit.y; }
        }
      }
    }
    if (bestE < 0) return null;
    return { t: bestT, edge: bestE, x: bx, y: by };
  }

  /**
   * The nearest existing node (≠a,≠b) lying within `band` metres of the INTERIOR
   * of segment a→(pu,pv), or -1. A segment passing over a node without junctioning
   * there is a T-junction crossing (rule #0) — the grower reroutes/rejects on this.
   * The 20 m primary lattice makes exact-collinear node hits common, so this closes
   * the gap the edge-crossing scan (which excludes t≈1 endpoints) leaves open.
   */
  nodeOnSegment(au: number, av: number, pu: number, pv: number, na: number, nb: number, band: number): number {
    const dx = pu - au, dy = pv - av;
    const l2 = dx * dx + dy * dy;
    if (l2 < EPS) return -1;
    const gx0 = Math.floor((Math.min(au, pu) - band) / NODE_GRID_M), gx1 = Math.floor((Math.max(au, pu) + band) / NODE_GRID_M);
    const gy0 = Math.floor((Math.min(av, pv) - band) / NODE_GRID_M), gy1 = Math.floor((Math.max(av, pv) + band) / NODE_GRID_M);
    let best = -1, bestT = Infinity;
    const seen = new Set<number>();
    for (let gx = gx0; gx <= gx1; gx++) {
      for (let gy = gy0; gy <= gy1; gy++) {
        const bkt = this.ngrid.get(gx + "," + gy);
        if (bkt === undefined) continue;
        for (let bi = 0; bi < bkt.length; bi++) {
          const idx = bkt[bi];
          if (idx === na || idx === nb || seen.has(idx)) continue;
          seen.add(idx);
          const t = ((this.nu[idx] - au) * dx + (this.nv[idx] - av) * dy) / l2;
          if (t <= 0.02 || t >= 0.98) continue; // strictly interior
          const px = au + t * dx, py = av + t * dy;
          const perp = pmath.hypot(this.nu[idx] - px, this.nv[idx] - py);
          if (perp <= band && t < bestT) { bestT = t; best = idx; }
        }
      }
    }
    return best;
  }

  /** True iff segment a→b crosses/touches ANY alive edge not incident to na/nb (safety net). */
  anyCrossing(au: number, av: number, bu: number, bv: number, na: number, nb: number): boolean {
    return this.nearestCrossing(au, av, bu, bv, na, nb) !== null;
  }

  /**
   * True iff point (u,v) lies within `r` metres of the INTERIOR of any alive edge
   * not incident to na/nb — i.e. a node placed here would trip the planarity
   * repair's node-on-edge split (rule #0 sub-pass A). Used by the R1 de-quant
   * fallback to keep curved primaries clear of the existing net (planar by
   * construction, so the repair does near-nothing). Any-hit over the edge grid.
   */
  pointNearEdge(u: number, v: number, r: number, na: number, nb: number): boolean {
    const gx0 = Math.floor((u - r) / EDGE_GRID_M), gx1 = Math.floor((u + r) / EDGE_GRID_M);
    const gy0 = Math.floor((v - r) / EDGE_GRID_M), gy1 = Math.floor((v + r) / EDGE_GRID_M);
    const r2 = r * r;
    const seen = new Set<number>();
    for (let gx = gx0; gx <= gx1; gx++) {
      for (let gy = gy0; gy <= gy1; gy++) {
        const bkt = this.egrid.get(gx + "," + gy);
        if (bkt === undefined) continue;
        for (let bi = 0; bi < bkt.length; bi++) {
          const e = bkt[bi];
          if (seen.has(e)) continue;
          seen.add(e);
          if (!this.eAlive[e]) continue;
          const a = this.ea[e], b = this.eb[e];
          if (a === na || b === na || a === nb || b === nb) continue;
          const au = this.nu[a], av = this.nv[a], bu = this.nu[b], bv = this.nv[b];
          const dx = bu - au, dy = bv - av;
          const l2 = dx * dx + dy * dy;
          let t = l2 > EPS ? ((u - au) * dx + (v - av) * dy) / l2 : 0;
          if (t < 0) t = 0; else if (t > 1) t = 1;
          const cx = au + dx * t, cy = av + dy * t;
          const ex = u - cx, ey = v - cy;
          if (ex * ex + ey * ey <= r2) return true;
        }
      }
    }
    return false;
  }

  /**
   * The first proper crossing between two alive edges that share NO node, via the
   * edge grid (fixed edge-ascending order → deterministic). Returns {e1,e2,x,y} or
   * null. Used only by the planarity-repair fixpoint (rule #0 postcondition).
   */
  firstCrossing(): { e1: number; e2: number; x: number; y: number } | null {
    for (let e1 = 0; e1 < this.ea.length; e1++) {
      if (!this.eAlive[e1]) continue;
      const a = this.ea[e1], b = this.eb[e1];
      const au = this.nu[a], av = this.nv[a], bu = this.nu[b], bv = this.nv[b];
      const gx0 = Math.floor(Math.min(au, bu) / EDGE_GRID_M), gx1 = Math.floor(Math.max(au, bu) / EDGE_GRID_M);
      const gy0 = Math.floor(Math.min(av, bv) / EDGE_GRID_M), gy1 = Math.floor(Math.max(av, bv) / EDGE_GRID_M);
      const seen = new Set<number>();
      for (let gx = gx0; gx <= gx1; gx++) {
        for (let gy = gy0; gy <= gy1; gy++) {
          const bkt = this.egrid.get(gx + "," + gy);
          if (bkt === undefined) continue;
          for (let bi = 0; bi < bkt.length; bi++) {
            const e2 = bkt[bi];
            if (e2 <= e1 || seen.has(e2) || !this.eAlive[e2]) continue;
            seen.add(e2);
            const c = this.ea[e2], d = this.eb[e2];
            if (a === c || a === d || b === c || b === d) continue;
            const hit = segInt(au, av, bu, bv, this.nu[c], this.nv[c], this.nu[d], this.nv[d]);
            if (hit !== null) return { e1, e2, x: hit.x, y: hit.y };
          }
        }
      }
    }
    return null;
  }

  /**
   * The min angle (rad) between the unit direction `du,dv` (leaving node `i`) and
   * any edge incident to `i`. π when `i` has no incident edges (no constraint).
   */
  minIncidentAngle(i: number, du: number, dv: number): number {
    const list = this.inc[i];
    let best = Math.PI;
    for (let t = 0; t < list.length; t++) {
      const e = list[t];
      const other = this.ea[e] === i ? this.eb[e] : this.ea[e];
      let ou = this.nu[other] - this.nu[i], ov = this.nv[other] - this.nv[i];
      const l = pmath.hypot(ou, ov);
      if (l < EPS) continue;
      ou /= l; ov /= l;
      let dot = du * ou + dv * ov;
      if (dot > 1) dot = 1; else if (dot < -1) dot = -1;
      const ang = pmath.acos(dot);
      if (ang < best) best = ang;
    }
    return best;
  }

  /**
   * Freeze survivors into a fresh builder (primaries unlogged @ epoch 0, secondaries
   * LOG_EXTEND @ era-banded epoch). Edges are emitted in (epoch asc, creation asc)
   * order so a split-primary half — added late but epoch-0 — never regresses the
   * "edge epochs non-decreasing" invariant, and old-core (lower-epoch) streets sort
   * ahead of outer ones (era banding survives into the frozen graph).
   */
  flush(radius: number, regionId: CellId): CityGraphBuilder {
    const nb = new CityGraphBuilder(radius, regionId);
    const remap = new Int32Array(this.nu.length).fill(-1);
    for (let i = 0; i < this.nu.length; i++) remap[i] = nb.addNode(this.nu[i], this.nv[i], this.nk[i]);
    const alive: number[] = [];
    for (let e = 0; e < this.ea.length; e++) if (this.eAlive[e]) alive.push(e);
    alive.sort((x, y) => (this.eEpoch[x] - this.eEpoch[y]) || (x - y));
    for (let t = 0; t < alive.length; t++) {
      const e = alive[t];
      const ne = nb.addEdge(remap[this.ea[e]], remap[this.eb[e]], this.ek[e], this.eEpoch[e]);
      if (this.eLogged[e]) nb.log(LOG_EXTEND, ne, 0);
    }
    return nb;
  }
}

/** Intersection of segment AB with CD; params t on AB, u on CD both in [0,1]. */
function segInt(
  ax: number, ay: number, bx: number, by: number,
  cx: number, cy: number, dx: number, dy: number,
): { t: number; u: number; x: number; y: number } | null {
  const rx = bx - ax, ry = by - ay, sx = dx - cx, sy = dy - cy;
  const denom = rx * sy - ry * sx;
  if (denom < EPS && denom > -EPS) return null; // parallel/collinear — treat as no proper crossing
  const t = ((cx - ax) * sy - (cy - ay) * sx) / denom;
  const u = ((cx - ax) * ry - (cy - ay) * rx) / denom;
  if (t < EPS || t > 1 - EPS || u < EPS || u > 1 - EPS) return null; // endpoints excluded
  return { t, u, x: ax + t * rx, y: ay + t * ry };
}

/** Angle (rad) between vector (ux,uy) and the UNIT direction (du,dv); π if degenerate. */
function angleTo(ux: number, uy: number, du: number, dv: number): number {
  const l = pmath.hypot(ux, uy);
  if (l < EPS) return Math.PI;
  let dot = (ux * du + uy * dv) / l;
  if (dot > 1) dot = 1; else if (dot < -1) dot = -1;
  return pmath.acos(dot);
}

// ── the era grower ─────────────────────────────────────────────────────────────

interface Front { node: number; hu: number; hv: number; dist: number; seq: number; }

/** Min-heap of fronts: distance-from-seed ascending, insertion-seq tie-break. */
class FrontHeap {
  private h: Front[] = [];
  private less(a: Front, b: Front): boolean {
    if (a.dist !== b.dist) return a.dist < b.dist;
    return a.seq < b.seq;
  }
  get size(): number { return this.h.length; }
  push(f: Front): void {
    this.h.push(f);
    let i = this.h.length - 1;
    while (i > 0) { const p = (i - 1) >> 1; if (this.less(this.h[i], this.h[p])) { const t = this.h[i]; this.h[i] = this.h[p]; this.h[p] = t; i = p; } else break; }
  }
  pop(): Front {
    const top = this.h[0], last = this.h.pop()!;
    if (this.h.length > 0) {
      this.h[0] = last; let i = 0;
      for (;;) {
        const l = 2 * i + 1, r = 2 * i + 2; let m = i;
        if (l < this.h.length && this.less(this.h[l], this.h[m])) m = l;
        if (r < this.h.length && this.less(this.h[r], this.h[m])) m = r;
        if (m === i) break;
        const t = this.h[i]; this.h[i] = this.h[m]; this.h[m] = t; i = m;
      }
    }
    return top;
  }
}

/**
 * The min junction angle (rad) for a settlement — the tier table value lerped 50%
 * toward the culture's `minAngleDeg` (ORCHESTRATOR RESOLUTION of the brief
 * ambiguity: the SAME 50%-culture modulation the tensor module applies to gridW /
 * heading noise, i.e. ½·(table + culture.minAngleDeg)).
 */
export function minAngleRad(p: SecondaryParams, cultureMinAngleDeg: number): number {
  return (p.minAngleDeg + 0.5 * (cultureMinAngleDeg - p.minAngleDeg)) * DEG2RAD;
}

/**
 * Grow every settlement's secondary street network (+ any co-located hamlet lane)
 * into a shared {@link PlanarNet} snapshotted from `builder`'s primaries, then
 * flush a fresh builder. See the module header for the full contract.
 *
 * `terrain` is the ONE shared memoized {@link RouteTerrain} the router + tensor
 * field both read (orchestrator note: build once per region, pass to both).
 *
 * `budgetOverride` (TEST-ONLY seam) forces every settlement's per-tier iteration
 * budget to a fixed value INSTEAD of the hash roll — used by the truncation oracle
 * to grow a tiny-budget vs full-budget net and prove edge-first degradation. It is
 * INERT when omitted (the hash roll runs exactly as before), so no digest shifts.
 *
 * `repairOut` (TEST-ONLY seam) collects the (u,v) of every node the final planarity
 * REPAIR pass touches (a node-on-edge split's node, or a new crossing junction) —
 * the ONLY joins that bypass the min-angle gate (rule #0 precedes the heuristic).
 * The angle oracle exempts these and asserts 0 violations everywhere else. Purely an
 * out-parameter: the returned builder is byte-identical whether or not it is passed.
 */
export function growSecondaryNetwork(
  sampler: PlanetSampler,
  cell: CellId,
  frame: RegionFrame,
  builder: CityGraphBuilder,
  seeds: { u: number; v: number; k: number; tier: number }[],
  terrain: RouteTerrain,
  budgetOverride?: number,
  repairOut?: number[],
): CityGraphBuilder {
  const planetHash = hashSeed(sampler.def.seed);
  const cellKey = packCell(cell);
  const radius = frame.radius;

  // Snapshot the primary network into a mutable planar net (primaries epoch 0,
  // unlogged — carry #3; they remain the oldest infrastructure with no log entry).
  const g = builder.build();
  const net = new PlanarNet();
  for (let i = 0; i < g.nodeU.length; i++) net.addNode(g.nodeU[i], g.nodeV[i], g.nodeKind[i]);
  for (let e = 0; e < g.edgeA.length; e++) net.addEdge(g.edgeA[e], g.edgeB[e], g.edgeKind[e], g.edgeEpoch[e], false);

  // A running era-banded epoch counter: starts ABOVE the primaries' epoch 0 so
  // every secondary is younger; increments as segments are ACCEPTED in priority
  // (inner-first) order, so old-core edges carry the lower epochs (banded).
  const epochBox = { next: 1 };

  // Wet guard: streets are dry infrastructure (no bridges among secondaries).
  const wetDir = new Float64Array(3);
  const wetSamp: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  const isWet = (u: number, v: number): boolean => {
    if (sampler.seaRel === 0) return false;
    localToDir(frame, u, v, wetDir);
    sampler.sample(wetDir[0], wetDir[1], wetDir[2], wetSamp, false);
    return wetSamp.h <= sampler.seaRel;
  };

  // Streets are CELL-LOCAL infrastructure: the union-planarity contract (R1
  // ORACLE 2) is only checkable against the OWN cell's net, so a segment whose
  // endpoint lands in a neighbour cell is unverifiable there — and was caught
  // live crossing a neighbour's arterial 21 m past the seam (3:-1:2 Voxes
  // block (0,10,2), exposed when the v16 steep-site gate re-sited villages
  // toward a border). Same exact ownership predicate the primary de-quant uses.
  const inCell = makeInCell(sampler, cell, frame);

  // Grow each seed (ascending k already, but be explicit) into the shared net.
  const order = [...seeds].sort((a, b) => a.k - b.k);
  for (const s of order) {
    if (s.tier >= TIER_VILLAGE) {
      growOne(net, planetHash, cellKey, s, SECONDARY_PARAMS[s.tier], terrain, isWet, inCell, epochBox, budgetOverride);
    } else if (s.tier === TIER_HAMLET) {
      growOne(net, planetHash, cellKey, s, HAMLET_LANE, terrain, isWet, inCell, epochBox, budgetOverride);
    }
    // WAYSTATION seeds in a village+ cell: no network (spec §"Tiers 1–2").
  }

  // Final planarity REPAIR (rule #0 guarantee): the grower's per-segment gates
  // catch every crossing AT INSERTION, but deferred splits can leave a rare residual
  // (a near-coincident junction, or two collinear primary sub-segments). This pass
  // resolves any remaining crossing into a SHARED junction — geometry never moves,
  // so it introduces no new crossing — making crossings==0 a hard postcondition.
  repairPlanarity(net, repairOut);

  // R1 D3: DE-QUANTIZE the primary CHAINS (jitter → straight-run cap → Chaikin) on
  // the SHARED planar net — junctions/anchors/street-attachment nodes are fixed, so
  // topology is preserved and no shared trunk separates (the per-polyline hazard).
  // Streets grew off the CLEAN lattice primaries above, so their count is unaffected;
  // only the emitted primary geometry curves. Repair again to junction any crossing
  // the curved primaries introduce (crossings==0 stays a hard postcondition).
  // De-quant is PLANAR BY CONSTRUCTION (each curved chain is accepted only if it
  // clears the net), so NO second repair is run here — repair's node-on-edge split
  // cascades on dense cells. `repairOut` (test seam) still reflects the growth-phase
  // repair above; the de-quant adds no repair junctions.
  const curved = dequantizePrimaryNet(net, planetHash, cellKey, terrain, isWet, inCell);

  return curved.flush(radius, cell);
}

/**
 * R1 Task 1.5 — give a HAMLET cell's through-roads/legs/links PLANARITY DISCIPLINE so
 * they can be DE-QUANTIZED safely (the T1 fix wave froze hamlet lanes on the raw 45°-
 * quantized lattice — 75.3% collinear, 47 straight runs ≥240 m — because curving them
 * with NO planar net created up to 23 self-crossings/cell; oracle 4 guards that at 0).
 *
 * The hamlet builder here carries ONLY roads (arterials/bridges + union-find links) —
 * cottages/parcels are placed AFTER this pass — so the flush that rebuilds the graph
 * has no parcels/logs to preserve (every hamlet road edge is unlogged, epoch 0). The
 * pass mirrors {@link growSecondaryNetwork}'s primary tail EXACTLY: snapshot the road
 * graph into a {@link PlanarNet}, {@link repairPlanarity} it (the hamlet cell gains the
 * planar net it lacked — this junctions any raw-lattice T-touch/overlap), then run the
 * SAME de-quant ladder ({@link dequantizePrimaryNet}: jitter → straight-run cap →
 * Chaikin, planar-by-construction, cap-only floor) on the arterial chains, and flush.
 * Because de-quant is planar-by-construction (each curved chain accepted only if it
 * clears the net) and the follow-up repair is intentionally skipped, the curved hamlet
 * road graph stays self-planar — oracle 4 (0 self-crossings) holds while the geometry-
 * character oracle (collinear <15%, 0 runs ≥240 m) is finally met for hamlets too.
 *
 * Returns a FRESH builder (curved roads); the caller places cottages on it. Same salts
 * as VILLAGE+ (SALT_JITTER/SALT_BEND — identical draw semantics), fixed order,
 * pmath-only, no Date/Math.random. No-op (returns a rebuilt-but-identical graph) if the
 * cell has no primary edges.
 */
export function curveHamletPrimaries(
  sampler: PlanetSampler, cell: CellId, frame: RegionFrame, builder: CityGraphBuilder,
): CityGraphBuilder {
  const planetHash = hashSeed(sampler.def.seed);
  const cellKey = packCell(cell);
  const radius = frame.radius;
  const g = builder.build();

  // Snapshot the road graph into a mutable planar net (preserving kind/epoch/logged).
  const net = new PlanarNet();
  for (let i = 0; i < g.nodeU.length; i++) net.addNode(g.nodeU[i], g.nodeV[i], g.nodeKind[i]);
  for (let e = 0; e < g.edgeA.length; e++) net.addEdge(g.edgeA[e], g.edgeB[e], g.edgeKind[e], g.edgeEpoch[e], false);

  const terrain: RouteTerrain = makeSamplerTerrain(sampler, frame);
  const wetDir = new Float64Array(3);
  const wetSamp: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  const isWet = (u: number, v: number): boolean => {
    if (sampler.seaRel === 0) return false;
    localToDir(frame, u, v, wetDir);
    sampler.sample(wetDir[0], wetDir[1], wetDir[2], wetSamp, false);
    return wetSamp.h <= sampler.seaRel;
  };

  // Planarity repair FIRST (the hamlet cell had no planar net): junction any raw-lattice
  // T-touch/overlap so the de-quant starts from a clean planar graph.
  repairPlanarity(net);
  // Then the SAME de-quant ladder VILLAGE+ primaries use — planar by construction.
  const inCell = makeInCell(sampler, cell, frame);
  const curved = dequantizePrimaryNet(net, planetHash, cellKey, terrain, isWet, inCell);
  // Repair AFTER de-quant too. VILLAGE+ SKIPS this (its dense cells cascade on the
  // node-on-edge split), but a hamlet cell is SPARSE — a handful of roads — so two
  // near-parallel through-roads whose de-quant fell to the (unvalidated) cap-only floor
  // can weave a few proper crossings; a cheap repair junctions each into a shared 4-way,
  // restoring the self-planarity oracle 4 requires. This is exactly the "planarity
  // discipline" the T1 review prescribed to make hamlet de-quant safe.
  repairPlanarity(curved);
  return curved.flush(radius, cell);
}

/**
 * R1 D3 — de-quantize the ARTERIAL/BRIDGE (primary) chains of a grown planar net,
 * returning a FRESH {@link PlanarNet} (moving nodes in place would corrupt the
 * spatial hash). A node is FIXED when it is a SEED/PORT anchor, a primary junction
 * (primary-degree != 2), or carries any street edge — so topology and every street
 * attachment survive. Each maximal fixed→…→fixed primary chain is extracted as a
 * polyline and run through {@link dequantizePrimary} (jitter ≤6 m, keyed on each
 * node's 20 m lattice cell, slope-aware + border-aware via the `inCell` predicate;
 * 120 m straight-run bend cap; Chaikin ×1), then re-emitted as a chain of short
 * ARTERIAL/BRIDGE edges (BRIDGE where a wet
 * endpoint, matching the emit rule). Chains share only their FIXED endpoints, so no
 * two curves separate a shared trunk — the per-polyline explosion cannot recur.
 * Streets/other edges are copied verbatim (endpoints are fixed by construction).
 */
function dequantizePrimaryNet(
  net: PlanarNet, planetHash: number, cellKey: number,
  terrain: RouteTerrain, isWet: (u: number, v: number) => boolean,
  inCell: (u: number, v: number) => boolean,
): PlanarNet {
  const N = net.nu.length;
  const isPrim = (e: number): boolean => net.ek[e] === EDGE_ARTERIAL || net.ek[e] === EDGE_BRIDGE;
  const primInc: number[][] = Array.from({ length: N }, () => []);
  const hasStreet = new Uint8Array(N);
  for (let e = 0; e < net.ea.length; e++) {
    if (!net.eAlive[e]) continue;
    if (isPrim(e)) { primInc[net.ea[e]].push(e); primInc[net.eb[e]].push(e); }
    else { hasStreet[net.ea[e]] = 1; hasStreet[net.eb[e]] = 1; }
  }
  const fixed = (i: number): boolean =>
    net.nk[i] === NODE_SEED || net.nk[i] === NODE_PORT || hasStreet[i] === 1 || primInc[i].length !== 2;

  const dq = new PlanarNet();
  const remap = new Int32Array(N).fill(-1);
  const ensure = (i: number): number => { if (remap[i] < 0) remap[i] = dq.addNode(net.nu[i], net.nv[i], net.nk[i]); return remap[i]; };
  // Copy street/other edges verbatim (their endpoints are all fixed).
  for (let e = 0; e < net.ea.length; e++) {
    if (!net.eAlive[e] || isPrim(e)) continue;
    dq.addEdge(ensure(net.ea[e]), ensure(net.eb[e]), net.ek[e], net.eEpoch[e], net.eLogged[e]);
  }
  const heightM = (u: number, v: number): number => terrain.heightM(u, v);
  const edgeDone = new Uint8Array(net.ea.length);
  // Does adding `curve` (from endA to endB) cross/touch any edge ALREADY in dq? The
  // end segments exclude their shared-junction's incident edges; interior segments
  // touch nothing yet. This makes de-quant PLANAR BY CONSTRUCTION so the follow-up
  // repair does near-nothing (the Chaikin-in-a-dense-town crossing cascade is gone).
  const curveCrosses = (curve: { u: number; v: number }[], endA: number, endB: number): boolean => {
    for (let i = 1; i < curve.length; i++) {
      const a = curve[i - 1], b = curve[i];
      const from = i === 1 ? endA : (i === curve.length - 1 ? endB : -1);
      if (dq.nearestCrossing(a.u, a.v, b.u, b.v, from, -1) !== null) return true;
      if (dq.nodeOnSegment(a.u, a.v, b.u, b.v, endA, endB, NODE_ON_EDGE_M) >= 0) return true;
      // A new INTERIOR node within the node-on-edge band of an existing edge would
      // trip the repair's split cascade — reject so the fallback keeps clear.
      if (i < curve.length - 1 && dq.pointNearEdge(b.u, b.v, NODE_ON_EDGE_M, endA, endB)) return true;
    }
    return false;
  };
  // A candidate is usable if it clears the net. Grade stays ≤20° per-node INSIDE
  // dequantizePrimary (grade-scaled jitter + grade-repair pass + Chaikin corner guard +
  // grade-adaptive cap), so no separate whole-curve grade check is needed here — and
  // adding one would over-revert chains to the (un-jittered) cap-only floor, regressing
  // the geometry oracle. The worldgen-growth ≤20° primary-grade oracle stays green.
  const usable = (c: { u: number; v: number }[], a: number, b: number): boolean => !curveCrosses(c, a, b);
  const emitCurve = (fA: number, fB: number, poly: { u: number; v: number }[]): void => {
    const endA = ensure(fA), endB = ensure(fB);
    // Prefer the FIRST candidate that clears the net AND the grade rule: smooth Chaikin
    // → jitter+cap → CAP-ONLY (the floor — always breaks a >120 m straight run with a
    // grade-adaptive bend, so no dense-cell fallback leaves a ruler-straight primary).
    // The first two are PLANAR BY CONSTRUCTION (no follow-up repair, whose node-on-edge
    // split cascades on dense cells); the cap-only floor is grade-safe by construction
    // and may leave a rare harmless near-touch (never a proper crossing). Deterministic.
    let curve = dequantizePrimary(poly, planetHash, cellKey, LATTICE_M, heightM, CHAIKIN_ROUNDS, true, inCell);
    if (!usable(curve, endA, endB)) {
      curve = dequantizePrimary(poly, planetHash, cellKey, LATTICE_M, heightM, 0, true, inCell);
      if (!usable(curve, endA, endB)) curve = dequantizePrimary(poly, planetHash, cellKey, LATTICE_M, heightM, 0, false, inCell);
    }
    let prevIdx = endA;
    for (let i = 1; i < curve.length; i++) {
      const last = i === curve.length - 1;
      const idx = last ? endB : dq.addNode(curve[i].u, curve[i].v, NODE_JUNCTION);
      const kind = (isWet(curve[i - 1].u, curve[i - 1].v) || isWet(curve[i].u, curve[i].v)) ? EDGE_BRIDGE : EDGE_ARTERIAL;
      if (idx !== prevIdx) dq.addEdge(prevIdx, idx, kind, 0, false);
      prevIdx = idx;
    }
  };
  // Walk every maximal primary chain starting at a fixed node.
  for (let f = 0; f < N; f++) {
    if (!fixed(f)) continue;
    for (const start of primInc[f]) {
      if (edgeDone[start]) continue;
      edgeDone[start] = 1;
      let arriv = start, cur = net.ea[start] === f ? net.eb[start] : net.ea[start];
      const poly: { u: number; v: number }[] = [{ u: net.nu[f], v: net.nv[f] }, { u: net.nu[cur], v: net.nv[cur] }];
      while (!fixed(cur)) {
        const inc = primInc[cur];
        const ne = inc[0] === arriv ? inc[1] : inc[0];
        if (edgeDone[ne]) break;
        edgeDone[ne] = 1;
        const nx = net.ea[ne] === cur ? net.eb[ne] : net.ea[ne];
        poly.push({ u: net.nu[nx], v: net.nv[nx] });
        arriv = ne; cur = nx;
      }
      emitCurve(f, cur, poly);
    }
  }
  // Fixed-less primary CYCLES (a ring with no junction/anchor) are never reached by
  // the fixed-node walks. Break each at its lowest-index node (treat as a pseudo-fixed
  // endpoint) and de-quant the loop back to it — so a ring road is curved + straight-
  // run-capped like any chain, not copied ruler-straight.
  for (let f = 0; f < N; f++) {
    for (const start of primInc[f]) {
      if (edgeDone[start]) continue;
      edgeDone[start] = 1;
      let arriv = start, cur = net.ea[start] === f ? net.eb[start] : net.ea[start];
      const poly: { u: number; v: number }[] = [{ u: net.nu[f], v: net.nv[f] }, { u: net.nu[cur], v: net.nv[cur] }];
      while (cur !== f && primInc[cur].length === 2) {
        const inc = primInc[cur];
        const ne = inc[0] === arriv ? inc[1] : inc[0];
        if (edgeDone[ne]) break;
        edgeDone[ne] = 1;
        const nx = net.ea[ne] === cur ? net.eb[ne] : net.ea[ne];
        poly.push({ u: net.nu[nx], v: net.nv[nx] });
        arriv = ne; cur = nx;
      }
      emitCurve(f, cur, poly);
    }
  }
  // Preserve ISOLATED anchors: a NODE_PORT / NODE_SEED with no incident edge is not
  // reached by any chain walk, but MUST survive the rebuild — cross-region boundary
  // continuity requires every port node to exist (the flush this replaces copied ALL
  // nodes). Non-anchor isolated nodes are intentionally dropped (pruneDeadEnds would).
  for (let i = 0; i < N; i++) if (net.nk[i] === NODE_PORT || net.nk[i] === NODE_SEED) ensure(i);
  return dq;
}

/**
 * Resolve every residual edge crossing into a shared junction (rule #0 hard
 * postcondition). Two sub-passes, iterated to a fixpoint (bounded): (A) split any
 * edge that a non-incident node sits ON (T-junction / collinear overlap → the node
 * becomes the junction); (B) split BOTH edges of a proper crossing at one shared new
 * junction. Geometry is never moved, so no pass can create a new crossing; the loop
 * therefore strictly reduces the crossing count and terminates.
 *
 * `touchedOut` (test seam), when supplied, receives the (u,v) of every node this
 * pass junctions through — the joins that PRECEDE the min-angle gate (rule #0),
 * so the angle oracle can exempt them and assert 0 grower-gated violations.
 */
function repairPlanarity(net: PlanarNet, touchedOut?: number[]): void {
  const cap = 8 * (net.ea.length + 4);
  for (let round = 0; round < cap; round++) {
    // (A) node-on-edge: convert a T-touch/collinear overlap into a junction.
    let did = false;
    const edges = net.aliveEdges();
    for (let ti = 0; ti < edges.length; ti++) {
      const e = edges[ti];
      if (!net.eAlive[e]) continue;
      const a = net.ea[e], b = net.eb[e];
      const x = net.nodeOnSegment(net.nu[a], net.nv[a], net.nu[b], net.nv[b], a, b, NODE_ON_EDGE_M);
      if (x >= 0) { if (touchedOut) { touchedOut.push(net.nu[x], net.nv[x]); } net.splitEdgeUsing(e, x); did = true; }
    }
    if (did) continue;
    // (B) proper crossing. A SHALLOW crossing (< 15°) would junction into a thin
    // sliver that violates the min-angle read; if either edge is a removable STREET
    // we drop the YOUNGER street instead (primaries are never removed). Otherwise we
    // split both at ONE shared junction (a clean 4-way).
    const c = net.firstCrossing();
    if (c === null) break;
    const ang = net.crossAngle(c.e1, c.e2);
    const e1Street = net.ek[c.e1] === EDGE_STREET, e2Street = net.ek[c.e2] === EDGE_STREET;
    if (ang < REPAIR_MIN_ANGLE && (e1Street || e2Street)) {
      // drop the younger (higher-epoch) street; ties/only-one → the STREET one.
      const drop = e1Street && e2Street ? (net.eEpoch[c.e1] >= net.eEpoch[c.e2] ? c.e1 : c.e2) : (e1Street ? c.e1 : c.e2);
      net.retireEdge(drop);
    } else {
      const j = net.addNode(c.x, c.y, NODE_JUNCTION);
      if (touchedOut) { touchedOut.push(c.x, c.y); }
      net.splitEdgeUsing(c.e2, j); // e2 > e1, so e1's index stays valid
      net.splitEdgeUsing(c.e1, j);
    }
  }
}

/** Grow one settlement's secondary network into `net`. */
function growOne(
  net: PlanarNet, planetHash: number, cellKey: number,
  seed: { u: number; v: number; k: number; tier: number }, p: SecondaryParams,
  terrain: RouteTerrain, isWet: (u: number, v: number) => boolean,
  inCell: (u: number, v: number) => boolean,
  epochBox: { next: number }, budgetOverride?: number,
): void {
  const radiusM = TIER_PARAMS[seed.tier].radiusM;
  const culture = cultureFor(planetHash, cellKey, seed.k);
  const ctx: TensorCtx = {
    planetHash, cellKey, k: seed.k, seedU: seed.u, seedV: seed.v,
    radiusM, tier: seed.tier, gridTheta0: gridOrientationFor(planetHash, cellKey, seed.k),
    culture, terrain,
  };
  const minAng = minAngleRad(p, culture.minAngleDeg);
  const budget = budgetOverride !== undefined
    ? budgetOverride
    : p.budgetMin + Math.floor(h01(hashInts(planetHash, SALT_BUDGET, cellKey, seed.k)) * (p.budgetMax - p.budgetMin + 1));
  const nominal = (p.segMinM + p.segMaxM) * 0.5;
  // Era COUNT: the spec table gives a RANGE for some tiers (metropolis "4–5"); like
  // every other ranged table value it is hash-rolled per settlement in [eras,erasMax]
  // (SALT_ERAS). Fixed-count tiers (erasMax undefined) keep their single value.
  const erasHi = p.erasMax !== undefined ? p.erasMax : p.eras;
  const eras = erasHi > p.eras
    ? p.eras + Math.floor(h01(hashInts(planetHash, SALT_ERAS, cellKey, seed.k)) * (erasHi - p.eras + 1))
    : p.eras;

  const heap = new FrontHeap();
  let seq = 0;
  const distOf = (u: number, v: number): number => pmath.hypot(u - seed.u, v - seed.v);
  const eraOf = (dist: number): 0 | 1 | 2 => {
    let e = Math.floor((dist / Math.max(1, radiusM)) * eras);
    if (e < 0) e = 0; if (e > eras - 1) e = eras - 1;
    return (e > 2 ? 2 : e) as 0 | 1 | 2;
  };

  // Seed fronts from PRIMARY-road nodes within the settlement radius: each spawns
  // two fronts perpendicular to its incident primary (streets grow off the trunk).
  // The seed node itself (if present) seeds fronts along the tensor field.
  const r2 = radiusM * radiusM;
  const nInit = net.nu.length; // freeze: only pre-existing (primary) nodes seed fronts
  for (let i = 0; i < nInit; i++) {
    const du = net.nu[i] - seed.u, dv = net.nv[i] - seed.v;
    if (du * du + dv * dv > r2) continue;
    const inc = net.inc[i];
    // Perpendicular kicks off each incident PRIMARY edge (ARTERIAL/BRIDGE).
    let seeded = false;
    for (let t = 0; t < inc.length; t++) {
      const e = inc[t];
      if (net.ek[e] !== EDGE_ARTERIAL && net.ek[e] !== EDGE_BRIDGE) continue;
      const other = net.ea[e] === i ? net.eb[e] : net.ea[e];
      let ou = net.nu[other] - net.nu[i], ov = net.nv[other] - net.nv[i];
      const l = pmath.hypot(ou, ov);
      if (l < EPS) continue;
      ou /= l; ov /= l;
      heap.push({ node: i, hu: -ov, hv: ou, dist: distOf(net.nu[i], net.nv[i]), seq: seq++ });
      heap.push({ node: i, hu: ov, hv: -ou, dist: distOf(net.nu[i], net.nv[i]), seq: seq++ });
      seeded = true;
    }
    // A bare seed node (no primary, e.g. a lone-anchor village) kicks off along the
    // tensor field's two axes so growth still starts.
    if (!seeded && net.nk[i] === NODE_SEED) {
      const d0 = tensorDirAt(ctx, net.nu[i], net.nv[i], eraOf(distOf(net.nu[i], net.nv[i])));
      heap.push({ node: i, hu: d0.du, hv: d0.dv, dist: distOf(net.nu[i], net.nv[i]), seq: seq++ });
      heap.push({ node: i, hu: -d0.du, hv: -d0.dv, dist: distOf(net.nu[i], net.nv[i]), seq: seq++ });
    }
  }

  let segIndex = 0;
  for (let iter = 0; iter < budget && heap.size > 0; iter++) {
    const f = heap.pop();
    const nu = net.nu[f.node], nv = net.nv[f.node];
    if (net.degree(f.node) >= p.maxDegree) continue;

    const dist = distOf(nu, nv);
    const era = eraOf(dist);
    // Tensor direction at the front, oriented to the incoming heading, + noise.
    const tdir = tensorDirAt(ctx, nu, nv, era);
    let du = tdir.du, dv = tdir.dv;
    if (du * f.hu + dv * f.hv < 0) { du = -du; dv = -dv; } // orient forward
    const noiseRad = headingNoiseDeg(ctx, era, segIndex, f.node & 0xffff) * DEG2RAD;
    const ca = pmath.cos(noiseRad), sa = pmath.sin(noiseRad);
    const rdu = du * ca - dv * sa, rdv = du * sa + dv * ca;
    du = rdu; dv = rdv;

    const jitter = (h01(hashInts(planetHash, SALT_SEGLEN, cellKey, seed.k, segIndex)) * 2 - 1) * p.jitterFrac;
    segIndex++;
    const segLen = nominal * (1 + jitter);
    const reach = segLen * p.extendMult;

    const pnu = nu + du * segLen, pnv = nv + dv * segLen;      // nominal endpoint
    const fnu = nu + du * reach, fnv = nv + dv * reach;        // extend-to-meet far point

    // (1) snap-to-node near the nominal endpoint.
    const snap = net.nearestNode(pnu, pnv, p.snapRadiusM, f.node);
    const dSnap = snap >= 0 ? pmath.hypot(net.nu[snap] - nu, net.nv[snap] - nv) : Infinity;

    // (2) planar crossing scan over the extended segment (excluding f.node- and
    // snap-incident edges — a shared-node touch is legal).
    const cross = net.nearestCrossing(nu, nv, fnu, fnv, f.node, snap);
    const dCross = cross ? cross.t * reach : Infinity;

    // Decide the endpoint WITHOUT mutating (the split is deferred to commit so an
    // angle/degree/planarity reject never leaves a half-split edge): split at the
    // nearest crossing if it blocks (or meets) before the snap; else snap; else a
    // fresh node at the nominal length.
    // mode: 0 new node, 1 snap to `snapTo`, 2 split `splitE` at (eu,ev).
    let mode: number, snapTo = -1, splitE = -1;
    let eu: number, ev: number;
    if (cross && dCross <= dSnap) {
      if (dCross < 4) continue; // degenerate: crossing hugs the front node
      if (isWet(cross.x, cross.y)) continue;
      mode = 2; splitE = cross.edge; eu = cross.x; ev = cross.y;
    } else if (snap >= 0) {
      mode = 1; snapTo = snap; eu = net.nu[snap]; ev = net.nv[snap];
    } else {
      if (isWet(pnu, pnv)) continue;
      mode = 0; eu = pnu; ev = pnv;
    }

    // Route THROUGH any existing node lying on the segment interior (rule #0: a
    // street may not pass over a node without junctioning). Reroute to it (snap),
    // which shortens the segment; iterate until the path is node-clear. The 20 m
    // primary lattice makes this common (collinear lattice nodes on a straight run).
    for (let guard = 0; guard < 8; guard++) {
      const onNode = net.nodeOnSegment(nu, nv, eu, ev, f.node, mode === 1 ? snapTo : -1, NODE_ON_EDGE_M);
      if (onNode < 0) break;
      mode = 1; snapTo = onNode; splitE = -1; eu = net.nu[onNode]; ev = net.nv[onNode];
    }

    let su = eu - nu, sv = ev - nv;
    const sl = pmath.hypot(su, sv);
    if (sl < 6) continue; // too short to be a street
    // (2.4) cell confinement: streets never leave their owning cell (see the
    // growSecondaryNetwork note — neighbour territory is planarity-unchecked).
    if (!inCell(eu, ev)) continue;
    // (2.5) grade wall: streets never climb cliffs (memoized terrain — cheap).
    if (Math.abs(terrain.heightM(eu, ev) - terrain.heightM(nu, nv)) / sl > SECONDARY_MAX_GRADE) continue;
    su /= sl; sv /= sl;

    // (3) min-junction-angle at the FRONT node (existing incident streets/primaries).
    if (net.minIncidentAngle(f.node, su, sv) < minAng) continue;
    // ...and at the endpoint: a snap node's own incidents, or (for a split) the two
    // halves of the crossed edge that will meet the new street at the junction.
    if (mode === 1) {
      if (net.minIncidentAngle(snapTo, -su, -sv) < minAng) continue;
      if (net.degree(snapTo) >= p.maxDegree) continue;
    } else if (mode === 2) {
      const ca2 = net.ea[splitE], cb2 = net.eb[splitE];
      if (angleTo(net.nu[ca2] - eu, net.nv[ca2] - ev, -su, -sv) < minAng) continue;
      if (angleTo(net.nu[cb2] - eu, net.nv[cb2] - ev, -su, -sv) < minAng) continue;
    }

    // (4) max-degree at the front.
    if (net.degree(f.node) >= p.maxDegree) continue;

    // (4.5) d_sep: no near-parallel street within 0.42×segMin lateral that we
    // don't junction with (braid fix — see PlanarNet.parallelNearby). The
    // parallel RUN stops here, but the front is not wasted: it re-aims
    // PERPENDICULAR at the offending strand, and the standard pipeline turns
    // that into a split T-junction — strands MERGE instead of running
    // side-by-side (and instead of dying, which amputated dense fabric).
    {
      const par = net.parallelNearby(nu, nv, eu, ev, D_SEP_FRAC * p.segMinM, D_SEP_COS, f.node,
                                     mode === 1 ? snapTo : -1, mode === 2 ? splitE : -1);
      if (par !== null) {
        if (net.degree(f.node) < p.maxDegree) {
          let wu = par.mu - nu, wv = par.mv - nv;
          const along = wu * su + wv * sv; // strip the along-segment component
          wu -= su * along; wv -= sv * along;
          const wl = pmath.hypot(wu, wv);
          if (wl > EPS) heap.push({ node: f.node, hu: wu / wl, hv: wv / wl, dist: distOf(nu, nv), seq: seq++ });
        }
        continue;
      }
    }

    // Safety net (rule #0): the final segment must cross NOTHING (excluding edges
    // incident to its own endpoints). Cheap — one spatial-hash scan — and it closes
    // any residual T-junction the endpoint decision missed.
    if (net.anyCrossing(nu, nv, eu, ev, f.node, mode === 1 ? snapTo : -1)) continue;

    // Reject an exact duplicate edge (already adjacent) when snapping.
    if (mode === 1) {
      let dup = false;
      const inc = net.inc[f.node];
      for (let t = 0; t < inc.length; t++) { const e = inc[t]; if (net.ea[e] === snapTo || net.eb[e] === snapTo) { dup = true; break; } }
      if (dup || snapTo === f.node) continue;
    }

    // Commit: perform the deferred split (now that the segment is accepted), or
    // materialize the endpoint, then add + log the street.
    let endNode: number;
    if (mode === 2) endNode = net.splitEdge(splitE, eu, ev);
    else if (mode === 1) endNode = snapTo;
    else endNode = net.addNode(eu, ev, NODE_JUNCTION);
    if (endNode === f.node) continue;

    net.addEdge(f.node, endNode, EDGE_STREET, epochBox.next++, true);

    // Continue the front from the new endpoint (unless it snapped into an existing
    // saturated junction), plus a branch with the spec probability.
    const endDist = distOf(eu, ev);
    if (endDist <= radiusM && net.degree(endNode) < p.maxDegree) {
      heap.push({ node: endNode, hu: su, hv: sv, dist: endDist, seq: seq++ });
      const branchRoll = h01(hashInts(planetHash, SALT_BRANCH, cellKey, seed.k, segIndex));
      if (branchRoll < p.branchProb) {
        const side = h01(hashInts(planetHash, SALT_BRANCH, cellKey, seed.k, segIndex, 1)) < 0.5 ? 1 : -1;
        heap.push({ node: endNode, hu: -sv * side, hv: su * side, dist: endDist, seq: seq++ });
      }
    }
  }
}
